'use client';

import type * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { Slot as SlotPrimitive } from 'radix-ui';

import { buttonVariants } from './button';
import { useRipple } from './hooks/use-ripple';
import { cn } from './lib/cn';

const iconButtonVariants = cva('shrink-0 p-0', {
  variants: {
    size: {
      xs: 'size-4',
      sm: 'size-5',
      md: 'size-6',
      lg: 'size-8',
      xl: 'size-10',
      '2xl': 'size-12',
      '3xl': 'size-14',
      '4xl': 'size-16',
    },
  },
  defaultVariants: {
    size: 'lg',
  },
});

function IconButton({
  className,
  appearance = 'filled',
  color = 'neutral',
  size = 'lg',
  rounded = 'full',
  asChild = false,
  children,
  onClick,
  ...props
}: React.ComponentProps<'button'> &
  Omit<VariantProps<typeof buttonVariants>, 'size'> &
  VariantProps<typeof iconButtonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? SlotPrimitive.Slot : 'button';
  const { ripples, createRipple } = useRipple();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    createRipple(event);
    onClick?.(event);
  };

  return (
    <Comp
      className={cn(buttonVariants({ appearance, color, rounded }), iconButtonVariants({ size }), className)}
      data-slot="icon-button"
      onClick={asChild ? onClick : handleClick}
      type={asChild ? undefined : 'button'}
      {...props}>
      <SlotPrimitive.Slottable>{children}</SlotPrimitive.Slottable>
      {!asChild && (
        <span className="pointer-events-none absolute inset-0">
          {ripples.map((ripple) => (
            <span
              className="absolute animate-ripple rounded-full bg-current opacity-30"
              key={ripple.id}
              style={{
                left: ripple.x,
                top: ripple.y,
                width: ripple.size,
                height: ripple.size,
              }}
            />
          ))}
        </span>
      )}
    </Comp>
  );
}

export { IconButton };
